import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI, clubAPI, uploadAPI } from '../services/api';
import MapPicker from '../components/MapPicker';
import { useAlert } from '../components/Alert';
import './CreateLeague.css';

const CreateClub = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { showAlert } = useAlert();

  const [formData, setFormData] = useState({
    name: '',
    adminEmail: '',
    sport: 'Tennis (singles)',
    latitude: null,
    longitude: null,
  });

  const [logoFile, setLogoFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isAdmin) {
    navigate('/leagues');
    return null;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setLogoFile(file);
  };

  const handleLocationSelect = (position) => {
    setFormData((prev) => ({
      ...prev,
      latitude: position.lat,
      longitude: position.lng,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const { name, adminEmail, sport, latitude, longitude } = formData;

    if (!name.trim() || !adminEmail.trim()) {
      showAlert('Club name and admin email are required', 'warning');
      return;
    }

    if (!latitude || !longitude) {
      showAlert('Please select a location on the map', 'warning');
      return;
    }

    setLoading(true);

    try {
      // Find user by email
      const usersResponse = await adminAPI.getAllUsers();
      const allUsers = usersResponse.data || [];
      const normalizedEmail = adminEmail.trim().toLowerCase();
      const adminUser = allUsers.find(
        (u) => (u.email || '').toLowerCase() === normalizedEmail
      );

      if (!adminUser) {
        setLoading(false);
        showAlert('User not found with that email', 'error');
        return;
      }

      // Create club
      const createResponse = await clubAPI.createClub({
        name: name.trim(),
        adminId: adminUser.userId,
        sport: sport || 'Tennis (singles)',
        latitude,
        longitude,
      });

      const club = createResponse.data;

      // Optional: upload logo
      if (logoFile) {
        try {
          const uploadRes = await uploadAPI.uploadClubLogo(logoFile, club.clubId);
          await clubAPI.updateClub(club.clubId, {
            logo: uploadRes.data.url,
          });
        } catch (uploadErr) {
          console.warn('Club logo upload failed:', uploadErr);
        }
      }

      navigate(`/clubs/${club.clubId}`);
    } catch (err) {
      console.error('Create club error:', err);
      setError(err.response?.data?.error || 'Failed to create club');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">Create Club</h1>
          <p className="page-subtitle">Set up a new club and manage its players</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="create-league-card card">
          <form onSubmit={handleSubmit} className="create-league-form">
            <div className="form-row three-columns">
              <div className="form-group">
                <label className="form-label">Club Name *</label>
                <input
                  type="text"
                  name="name"
                  className="form-input"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Central Tennis Club"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Admin Email *</label>
                <input
                  type="email"
                  name="adminEmail"
                  className="form-input"
                  value={formData.adminEmail}
                  onChange={handleChange}
                  placeholder="admin@example.com"
                  required
                />
                <small className="form-hint">
                  The user with this email will be the club admin
                </small>
              </div>

              <div className="form-group">
                <label className="form-label">Sport *</label>
                <select
                  name="sport"
                  className="form-input sport-select"
                  value={formData.sport}
                  onChange={handleChange}
                  required
                >
                  <option value="Tennis (singles)">🎾 Tennis (singles)</option>
                  <option value="Tennis (doubles)" disabled>🎾 Tennis (doubles) (Coming Soon)</option>
                  <option value="Padel" disabled>🏓 Padel (Coming Soon)</option>
                  <option value="Squash">🏸 Squash</option>
                  <option value="Football" disabled>⚽ Football (Coming Soon)</option>
                  <option value="Basketball" disabled>🏀 Basketball (Coming Soon)</option>
                </select>
              </div>
            </div>

            <div className="form-row three-columns">
              <div className="form-group">
                <label className="form-label">Club Logo (optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  className="form-input"
                  onChange={handleLogoChange}
                />
                {logoFile && (
                  <small className="form-hint">Selected file: {logoFile.name}</small>
                )}
                <small className="form-hint">
                  Upload an image to display as the club logo. Square images work best.
                </small>
              </div>
              <div className="form-group"></div>
            </div>

            <div className="form-group">
              <label className="form-label">Select Location on Map *</label>
              <MapPicker
                onLocationSelect={handleLocationSelect}
                initialLocation={
                  formData.latitude && formData.longitude
                    ? { lat: formData.latitude, lng: formData.longitude }
                    : null
                }
              />
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Creating Club...' : 'Create Club'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/admin')}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateClub;


